import { useRouter } from 'next/router'
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react'

import classNames from 'classnames'
import ActiveLink from './ActiveLink'
import { ReactFC } from 'common/types/ReactFC'
import { THeaderLinkGroup } from 'common/cms-types'
import useOnClickOutside from 'project/hooks/useOnClickOutside'
import TrustedPartnershipBanner from './TrustedPartnershipBanner'

type HeaderNavButtonType = {
  headerLinkGroup: THeaderLinkGroup
}

const HeaderNavButton: ReactFC<HeaderNavButtonType> = ({ headerLinkGroup }) => {
  const ref = useRef(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const [active, setActive] = useState<boolean | string>(false)
  const [shouldFocusFirst, setShouldFocusFirst] = useState(false)

  const onClickOutside = () => {
    if (active) {
      setActive(false)
    }
  }

  useOnClickOutside(ref, onClickOutside, 'mouseover')
  const hasGroups = !!headerLinkGroup?.groups?.length
  const href = headerLinkGroup.href || headerLinkGroup.groups[0].links[0].href
  const idSlug = useMemo(
    () => headerLinkGroup.title.toLowerCase().replace(/\s+/g, '-'),
    [headerLinkGroup.title],
  )
  const menuId = useMemo(() => `menu-${idSlug}`, [idSlug])
  const triggerId = useMemo(() => `menu-button-${idSlug}`, [idSlug])

  useEffect(() => {
    if (active && shouldFocusFirst && menuRef.current) {
      const firstItem =
        menuRef.current.querySelector<HTMLElement>('[role="menuitem"]')
      if (firstItem) {
        firstItem.focus()
      }
      setShouldFocusFirst(false)
    }
  }, [active, shouldFocusFirst])

  // Ensure only one menu is open at a time across all HeaderNavButton instances
  useEffect(() => {
    const handleOtherMenuOpen = (e: any) => {
      if (e?.detail?.id && e.detail.id !== menuId) {
        setActive(false)
      }
    }
    window.addEventListener('header:menu-open', handleOtherMenuOpen)
    return () =>
      window.removeEventListener('header:menu-open', handleOtherMenuOpen)
  }, [menuId])

  const openMenu = useCallback(
    (focusFirst: boolean) => {
      // set to a unique truthy string to force re-render even if already truthy
      setActive(`true${Date.now()}`)
      if (focusFirst) setShouldFocusFirst(true)
      window.dispatchEvent(
        new CustomEvent('header:menu-open', { detail: { id: menuId } }),
      )
    },
    [menuId],
  )

  const closeMenu = useCallback(() => {
    setActive(false)
  }, [])

  return (
    <div
      className='nav-item cursor-pointer'
      onMouseEnter={() => {
        if (hasGroups) {
          openMenu(false) // non-VO users: open on hover
        }
      }}
      ref={ref}
    >
      <div
        onClick={(e) => {
          e.stopPropagation()
        }}
        className='dropdown-hover  h-100'
      >
        <ActiveLink
          className='a-unstyled d-flex align-items-center h-100'
          href={href}
          aria-haspopup={hasGroups ? 'menu' : undefined}
          aria-expanded={hasGroups ? !!active : undefined}
          aria-controls={hasGroups ? menuId : undefined}
          aria-label={`${headerLinkGroup.title} menu, ${
            active ? 'expanded' : 'collapsed'
          }`}
          id={triggerId}
          onClick={(e: React.MouseEvent) => {
            if (hasGroups) {
              e.preventDefault()
              if (!!active) {
                closeMenu()
              } else {
                openMenu(true)
              }
            }
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (!hasGroups) return
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              const willOpen = !active
              if (willOpen) {
                openMenu(true)
              } else {
                closeMenu()
              }
            }
            if (e.key === 'Escape') {
              closeMenu()
            }
          }}
        >
          <div
            data-test={`header-title-${headerLinkGroup.href}`}
            className={classNames(
              'nav-link-title d-flex align-items-center gap-1 cursor-pointer',
              {
                'me-1': !headerLinkGroup?.groups?.length,
              },
            )}
          >
            {headerLinkGroup.title}
            {!!headerLinkGroup?.groups?.length && (
              <svg
                width='12'
                height='8'
                viewBox='0 0 12 8'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M3.1219 4.61573L5.79615 6.92695C5.82271 6.95008 5.85441 6.96845 5.88939 6.981C5.92437 6.99354 5.96192 7 5.99985 7C6.03779 7 6.07534 6.99354 6.11032 6.981C6.1453 6.96845 6.177 6.95008 6.20356 6.92695L8.87781 4.61573C8.95612 4.54766 9 4.45604 9 4.36061C9 4.26517 8.95612 4.17355 8.87781 4.10548C8.83928 4.07208 8.79335 4.04554 8.7427 4.02743C8.69205 4.00933 8.63769 4 8.58278 4C8.52788 4 8.47352 4.00933 8.42287 4.02743C8.37222 4.04554 8.32629 4.07208 8.28776 4.10548L5.99985 6.08271L3.71253 4.10548C3.67395 4.0721 3.628 4.04559 3.57732 4.0275C3.52665 4.0094 3.47228 4.00009 3.41736 4.00009C3.36244 4.00009 3.30807 4.0094 3.2574 4.0275C3.20673 4.04559 3.16077 4.0721 3.12219 4.10548C3.04388 4.17355 3 4.26517 3 4.36061C3 4.45604 3.04359 4.54766 3.1219 4.61573Z'
                  fill='#1D1D1F'
                />
              </svg>
            )}
          </div>
        </ActiveLink>
        {!!hasGroups && (
          <div
            id={menuId}
            role='menu'
            aria-hidden={!active}
            className={classNames('dropdown-hover-show', { in: active })}
            aria-labelledby={triggerId}
            ref={menuRef}
          >
            <div
              className={classNames('dropdown-hover-content', {
                'dropdown-hover-content__single-item':
                  headerLinkGroup.groups.length === 1,
              })}
            >
              <div className='row mx-0'>
                {headerLinkGroup.groups.map((group) => {
                  if (group.title === 'Partnership') {
                    return (
                      <div className='nav-group pt-0'>
                        <TrustedPartnershipBanner />
                      </div>
                    )
                  }
                  return (
                    <ul
                      className={classNames('list-unstyled nav-group')}
                      key={group.title}
                    >
                      {group.title && (
                        <li
                          role='presentation'
                          className='dropdown-title text-semibold text-grey5 text-nowrap'
                        >
                          {group.title}
                        </li>
                      )}
                      {group.links.map((link, i) => {
                        const linkHref = link.href
                        return (
                          <>
                            {link.groupTitle && (
                              <li
                                role='presentation'
                                className='dropdown-title mt-4 text-semibold text-grey5 text-nowrap'
                              >
                                {link.groupTitle}
                              </li>
                            )}
                            <li
                              role='none'
                              data-test={`header-link-${linkHref}`}
                              className='text-nowrap nav-link cursor-pointer ps-0'
                              key={i}
                            >
                              <ActiveLink
                                className='a-unstyled'
                                href={linkHref}
                                role='menuitem'
                                tabIndex={active ? 0 : -1}
                              >
                                <div>
                                  <span
                                    className='d-flex align-items-center'
                                    onClick={(e) => {
                                      setActive(false)
                                    }}
                                    aria-current='page'
                                  >
                                    {link.title}
                                    {link.label && (
                                      <>
                                        <span className='fs-12 ms-1 d-flex align-items-center py-half pe-0 px-2 bg-pink text-white fw-semibold rounded'>
                                          {link.label}
                                          <svg
                                            className=''
                                            xmlns='http://www.w3.org/2000/svg'
                                            width='16'
                                            height='16'
                                            viewBox='0 0 24 24'
                                            fill='none'
                                          >
                                            <path
                                              d='M17.7576 7.5791C17.3697 7.00941 16.9726 5.96823 16.7576 6.07899C16.4276 6.24899 16.4375 7.7291 16.5475 8.0791C16.6375 8.3491 16.7475 8.95918 16.5475 9.55918C16.4275 9.92918 16.2175 10.2092 15.9975 10.4192C15.7775 9.01918 15.2675 7.28918 14.1075 5.59918C13.2975 4.41918 12.2775 3.39918 11.0875 2.57918C10.8375 2.39918 10.0076 1.89899 9.75757 2.07899C9.49757 2.24899 9.86746 3.05918 9.93746 3.35918C10.1875 4.48918 10.1875 5.67918 9.93746 6.80918C9.77746 7.53918 9.52746 8.17918 9.23746 8.72918C9.05746 8.27918 8.78746 7.80918 8.36746 7.38918C7.54746 6.56918 6.05746 6.22918 6.05746 6.22918C6.05746 6.22918 4.92757 5.89894 4.75757 6.07894C4.58757 6.25894 5.03964 6.35804 5.21746 7.13918C5.49746 8.36918 5.49746 9.51918 5.21746 10.5492C5.10746 10.9692 4.96746 11.2892 4.82746 11.6192C4.61746 12.1092 4.40746 12.6192 4.25746 13.4392C4.20746 13.7292 3.95746 15.2292 4.25746 16.5692C4.83746 19.1192 7.53746 21.3992 10.5375 21.8592C10.5575 21.8592 10.5875 21.8592 10.6075 21.8592C10.9975 21.9392 11.3475 21.9592 11.6175 21.9592C11.8275 21.9592 11.9975 21.9492 12.0775 21.9392C12.1575 21.9392 12.2375 21.9092 12.3175 21.8992C15.2675 21.6492 17.6975 19.7292 18.8475 17.7192C19.8475 15.9692 19.8775 14.2392 19.8875 13.5892C19.9008 12.7212 19.1769 9.66355 17.7576 7.5791Z'
                                              fill='#FFFFFF'
                                            />
                                            <path
                                              d='M9.88452 17.0791H8.97925V15.2305H7.28296V17.0791H6.37476V12.7959H7.28296V14.4746H8.97925V12.7959H9.88452V17.0791Z'
                                              fill='white'
                                            />
                                            <path
                                              d='M14.3562 14.9316C14.3562 15.6406 14.1804 16.1855 13.8289 16.5664C13.4773 16.9473 12.9734 17.1377 12.3171 17.1377C11.6609 17.1377 11.157 16.9473 10.8054 16.5664C10.4539 16.1855 10.2781 15.6387 10.2781 14.9258C10.2781 14.2129 10.4539 13.6689 10.8054 13.2939C11.1589 12.917 11.6648 12.7285 12.323 12.7285C12.9812 12.7285 13.4841 12.918 13.8318 13.2969C14.1814 13.6758 14.3562 14.2207 14.3562 14.9316ZM11.2302 14.9316C11.2302 15.4102 11.321 15.7705 11.5027 16.0127C11.6843 16.2549 11.9558 16.376 12.3171 16.376C13.0417 16.376 13.4041 15.8945 13.4041 14.9316C13.4041 13.9668 13.0437 13.4844 12.323 13.4844C11.9617 13.4844 11.6892 13.6064 11.5056 13.8506C11.322 14.0928 11.2302 14.4531 11.2302 14.9316Z'
                                              fill='white'
                                            />
                                            <path
                                              d='M16.3962 17.0791H15.488V13.5518H14.325V12.7959H17.5593V13.5518H16.3962V17.0791Z'
                                              fill='white'
                                            />
                                          </svg>
                                        </span>
                                      </>
                                    )}
                                  </span>
                                  {link.subtitle && (
                                    <div className='fs-small fw-normal fs-14'>
                                      {link.subtitle}
                                    </div>
                                  )}
                                </div>
                              </ActiveLink>
                            </li>
                          </>
                        )
                      })}
                    </ul>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HeaderNavButton
